import { FC } from 'react';
import { Col, Form } from 'antd';
import { ControlType, hasAreaProtectionAndAddress } from './config';
// 各类型组件
import Radio from './components/Radio';
import Checkbox from './components/Checkbox';
import DropdownSelect from './components/DropdownSelect';
import TreeSelect from './components/TreeSelect';
import Input from './components/Input';
import TextareaInput from './components/TextareaInput';
import Upload from './components/Upload';
import NumberInput from './components/NumberInput';
import Time from './components/Time';
import Address from './components/Address/index';
import Provinces from './components/Provinces';
import Footprint from './components/Footprint';
import Surround from './components/Surround';
import ContendInfo from './components/ContendInfo';
import BusinessPlanning from './components/BusinessPlanning';
import SaleAmount from './components/SaleAmount';
import BusinessInfo from './components/BusinessInfo';
import DailyFlow from './components/DailyFlow';
import RefConversion from './components/RefConversion';
import BenchBrands from './components/BenchBrands';
import CalculationJNP from './components/CalculationJNP';
import EstimateSalesKWKCom from './components/EstimateSalesKWKCom';
import EstimateSalesYHT from './components/EstimateSalesYHT';
import PopulationFlowYHT from './components/PopulationFlowYHT';
import EstimateSalesTwo from './components/YHT/EstimateSalesTwo';
import EstimateSalesPersonal from './components/YHT/EstimateSalesPersonal';
import EstimateSalesMTMean from './components/YHT/EstimateSalesMTMean';
import EstimateSalesMTMax from './components/YHT/EstimateSalesMTMax';
import EstimateSalesConclusion from './components/YHT/EstimateSalesConclusion';
import EstimateSalesDelivery from './components/YHT/EstimateSalesDelivery';
// hooks
import { useAccessIsDisabled } from './hooks/useAccessIsDisabled';
import { useAccessIsRequired } from './hooks/useAccessIsRequired';
import { useAccessIsShow } from './hooks/useAccessIsShow';
import PassengerFlowCounting from './components/PassengerFlow';
import CompetitiveConditions from './components/CompetitiveConditions';
import SubForm from './components/SubForm';
import FranchiseeCooperationProgress from './components/FranchiseeCooperationProgress';
import CompanyMember from './components/CompanyMember';
import AIPredictEntryRate from './components/AIPredictEntryRate/index';
import AreaProtection from './components/AreaProtection';
import LeaseTerm from './components/LeaseTerm';
import AssociatedRecords from './components/AssociatedRecords';
import BusinessTerms from './components/BusinessTerms';
import ProjectSpotSelect from './components/ProjectSpotSelect';
import { useMethods } from '@lhb/hook';

/**
 * @Description 一个动态组件
 * 区分不同的组件类型
 */
const PropertyComponent: FC<{
  item,
  form,
  wholeFormData,
  /** 是否机会点表单，加盟商、踩点表单时false */
  isChancepoint: boolean,
  /** 关联踩点记录仅在机会点创建、编辑表单中展示。 */
  showAssociatedRecords?: boolean,
  /** 是否传统的点位评估编辑页（不包含一鸣等租户走机会点点位评估审批流的场景），是的话item的显示、禁用，参照的是机会点拓店模板、非流程设置 */
  isTraditionEvaluation: boolean,
  addressChange,
  /** 地图移动事件 */
  mapMoveChange,
  /** 地图缩放事件 */
  mapZoomChange,
  /** 自定义组件（周边查询、商圈规划、周边竞品等）变动时，更新componentValue */
  updateCompValue,
  /** 选择框组件关联变更触发（比如 取消选中） */
  updateRelateShow,
  /** 是否支持直接提交审批 1:支持 2:不支持，默认值是2 */
  supportDirectApproval,
  /** 在编辑状态下，踩点组件是否已有数据（已有数据则设置表单不可读） */
  isFootprintValue: boolean,
  /** 商圈规划组件数据：地址变动时外部传入的商圈数据 */
  businessPlanValue,
  /** 商圈信息数据：地址变动时外部传入的商圈数据 */
  businessInfoValue,
  /** 单选、多选、输入框等值变更时的提示内容 */
  showHintStr,
  /** 是否有区域保护 */
  isAreaProtect,
  /** 当前区域保护特殊申请状态；未配置区域保护时保持 undefined。 */
  antiCollisionApprovalStatus?: number,
  /** 地址组件的值 */
  // addressVal,
  addressVal,
  salesFactorOfKWK,
  setSalesFactorOfKWK,
  /** 是否编辑过机会点名称 */
  isEditedBasicChancePointName,
  /** 更新：是否编辑过机会点名称 */
  setEditedBasicChancePointName,
  /** 所有组件字段平铺后的对象数组；使用 formDataComsToTiled 将动态表单中的所有组件平铺到数组中，方便查找到对应的组件 */
  tiledFormDataRef,
  customGatheringCategoryForYHT,
  setCustomGatheringCategoryForYHT,
  /** 禁用开店区域点击查看详情 */
  disabledArea,
  /** 编辑状态：true 编辑，false 新增 */
  isEdit?: boolean,
  /** 拓店任务商圈id */
  expansionTaskClusterId?,
  /** 区域保护检测结果变更后，更新详情顶部操作 */
  onAreaProtectionCollisionChange?: (hasCollisionWithin200m: boolean | undefined) => void,
}> = ({
  item,
  form,
  wholeFormData,
  isChancepoint = true, // 是否机会点表单，加盟商时false
  showAssociatedRecords = false,
  isTraditionEvaluation = false,
  addressChange,
  mapMoveChange, // 地图移动事件
  mapZoomChange, // 地图缩放事件
  updateCompValue,
  updateRelateShow,
  supportDirectApproval, // 是否支持直接提交审批 1:支持 2:不支持，默认值是2
  isFootprintValue = false, // 在编辑状态下，踩点组件是否已有数据（已有数据则设置表单不可读）
  businessPlanValue = {},
  businessInfoValue = {},
  /** 单选、多选、输入框等值变更时的提示内容 */
  showHintStr,
  isAreaProtect, // 是否有区域保护
  antiCollisionApprovalStatus,
  // addressVal, // 地址组件的值
  addressVal, // 地址组件的值
  salesFactorOfKWK,
  setSalesFactorOfKWK,
  isEditedBasicChancePointName,
  setEditedBasicChancePointName,
  tiledFormDataRef,
  customGatheringCategoryForYHT,
  setCustomGatheringCategoryForYHT,
  disabledArea, // 禁用开店区域点击查看详情
  isEdit = false, // 编辑状态
  expansionTaskClusterId,
  onAreaProtectionCollisionChange,
}) => {
  const hasAreaProtection = hasAreaProtectionAndAddress(wholeFormData?.propertyGroupVOList);
  const areaProtectionIdentification = hasAreaProtection ? tiledFormDataRef?.current?.find((target: any) => target.controlType === ControlType.AREA_PROTECTION.value)?.identification || '__areaProtection__' : '__areaProtection__';
  const areaProtectionValue: any = Form.useWatch(areaProtectionIdentification, form);
  // 特殊申请接口未提供前，审批状态不为 pending 即视为已发起特殊申请。
  const isAreaProtectionApplicationInitiated = hasAreaProtection && typeof areaProtectionValue?.approvalStatus === 'string' && areaProtectionValue.approvalStatus !== 'pending';
  // 详情接口已提供审批状态时，以接口状态为准；仅审核通过才锁定详细地址与地图选点。
  const isAreaProtectionAddressLocked = hasAreaProtection && (antiCollisionApprovalStatus === 1 || (antiCollisionApprovalStatus === undefined && isAreaProtectionApplicationInitiated));
  // 独占一行的字段
  const isColFullIdentifications = [
    'basicAddress'
  ];
  // 独占一行的组件类型
  const isColFullControlTypes = [
    ControlType.CHECK_BOX.value,
    ControlType.SURROUND_SEARCH.value,
    ControlType.SUB_FORM.value,
    ControlType.CONTEND_INFO.value,
    ControlType.COMPETITIVE_CONDITIONS.value,
    ControlType.BUSINESS_PLANNING.value,
    ControlType.MATCH_BUSINESS_CIRCLE.value,
    ControlType.DAILY_FLOW_PREDICT.value,
    ControlType.BENCH_MARKING_BRANDS.value,
    ControlType.PROFIT_CAlCULATION_JNP.value,
    ControlType.FRANCHISEE_COOPERATION_PROGRESS.value,
    ControlType.AI_PREDICT_ENTRY_RATE.value,
    ControlType.AREA_PROTECTION.value,
    ControlType.ASSOCIATED_RECORDS.value,
    ControlType.PROJECT_SPOT_SELECT.value,
    ControlType.RECOMMEND_CHANCE_POINT.value, // 为了不影响布局(实际上不会渲染该组件，该组件只在加盟商详情展示)
  ];
  // 定制组件内含多个组件时，外面不能包<Col>
  const targetMultipleComponent = [
    ControlType.DAILY_FLOW_PREDICT.value,
    ControlType.ESTIMATE_SALES_YHT.value,
    ControlType.PASSENGER_FLOW.value,
    ControlType.LEASE_TERM.value,
    ControlType.BUSINESS_CONDITION.value
  ];
  // 是否是换行显示
  const { isLineFeed } = useMethods({
    isLineFeed(item: any) {
      const { templateRestriction } = item;
      if (!templateRestriction) return false;
      try {
        const parsed = JSON.parse(templateRestriction);
        const { nextLine } = parsed || {};
        // 换行显示
        return nextLine;
      } catch (error) {}
      return false;
    },
  });

  /**
   * @description 是否禁用
   */
  const disabled = useAccessIsDisabled({
    taskEntry: wholeFormData?.taskEntry,
    supportDirectApproval,
    propertyItem: item,
    isTraditionEvaluation,
    isChancepoint,
  });

  /**
   * @description 是否必填
   */
  const required = useAccessIsRequired({
    propertyItem: item,
    isChancepoint,
    supportDirectApproval,
    // Locxx 入口按 access 过滤不可写字段，access=null 再沿用 supportDirectApproval 的原有分支
    // 不与 isTraditionEvaluation 耦合：该标志在拓店任务编辑场景下由 release 逻辑置 true，会让本分支失效
    writableOnly: wholeFormData?.taskEntry === true,
  });

  /**
   * @description 支持提交审批时是否可见，结合isShow最终决定是否展示
   */
  const isShowWhenApprovalProcess = useAccessIsShow({
    taskEntry: wholeFormData?.taskEntry,
    supportDirectApproval,
    propertyItem: item,
    isTraditionEvaluation,
    isChancepoint,
  });

  return (
    item.isShow && isShowWhenApprovalProcess && (item.controlType !== ControlType.AREA_PROTECTION.value || hasAreaProtection) && (item.controlType !== ControlType.ASSOCIATED_RECORDS.value || showAssociatedRecords) ? <>
      {/* 换行显示 */}
      {
        isLineFeed(item) ? (<Col span={24}></Col>) : null
      }
      {/*
        默认两列布局，但是部分场景下需要换行显示
      */}
      {
        // targetMultipleComponent中的定制组件内部包含多个字段值，因为排版问题，所以外层不能包Col
        targetMultipleComponent.includes(item.controlType) ? <>
          {
            item.controlType === ControlType.DAILY_FLOW_PREDICT.value ? <DailyFlow
              form={form}
              propertyItem={item}
              disabled={disabled}
              // required={required}
              updateCompValue={updateCompValue}
            /> : <></>
          }
          { // 益禾堂预估销售额
            item.controlType === ControlType.ESTIMATE_SALES_YHT.value ? <EstimateSalesYHT
              propertyItem={item}
              required={required}
              disabled={disabled}
              form={form}
              tiledFormDataRef={tiledFormDataRef}
            // addressVal={addressVal}
            // wholeFormData={wholeFormData}
            // infoChange={updateCompValue}
            /> : <></>
          }
          {item.controlType === ControlType.PASSENGER_FLOW.value ? <PassengerFlowCounting
            form={form}
            propertyItem={item}
            disabled={disabled}
            required={required}
            tiledFormDataRef={tiledFormDataRef}
            isEdit={isEdit}
          /> : <></>}
          {item.controlType === ControlType.LEASE_TERM.value ? <LeaseTerm
            propertyItem={item}
            form={form}
            disabled={disabled}
            required={required}/> : <></>}
          {item.controlType === ControlType.BUSINESS_CONDITION.value ? <BusinessTerms
            propertyItem={item}
            form={form}
            disabled={disabled}
            required={required}
            updateCompValue={updateCompValue}/> : <></>}
        </> : <Col span={
          isColFullIdentifications.includes(item.identification) ||
        isColFullControlTypes.includes(item.controlType) ? 24 : 12}>
          {item.controlType === ControlType.PROJECT_SPOT_SELECT.value ? <ProjectSpotSelect
            taskId={wholeFormData?.resourceProjectTaskId}
            projectSearchDefaults={wholeFormData?.resourceProjectSearchDefaults}
            tiledFormDataRef={tiledFormDataRef}
            updateRelateShow={updateRelateShow}
            propertyItem={item}
            form={form}
            disabled={disabled}
            required={required}
            updateCompValue={updateCompValue}/> : null}
          { // 单选组件,有两种展现形式,选项小于3项时用Raido，否则用select
            item.controlType === ControlType.SINGLE_RADIO.value &&
          (Array.isArray(item.propertyConfigOptionVOList) &&
          item.propertyConfigOptionVOList.length > 0 &&
          item.propertyConfigOptionVOList.length < 3 &&
          // 产品甜瓜：要求“加盟费-益禾堂”必须是下拉框
          item.identification !== 'franchiseFeeYHT'
            ? <Radio
              propertyItem={item}
              form={form}
              disabled={disabled}
              required={required}
              onClear={updateRelateShow}
              showHintStr={showHintStr}
              salesFactorOfKWK={salesFactorOfKWK}
              setSalesFactorOfKWK={setSalesFactorOfKWK}
            />
            : <DropdownSelect
              propertyItem={item}
              disabled={disabled}
              required={required}
              showHintStr={showHintStr}
              form={form}
              salesFactorOfKWK={salesFactorOfKWK}
              setSalesFactorOfKWK={setSalesFactorOfKWK}
              // updateRelateShow={updateRelateShow}
            />)
          }
          {
            // 多选组件
            item.controlType === ControlType.CHECK_BOX.value && <Checkbox propertyItem={item}
              disabled={disabled}
              required={required}
              showHintStr={showHintStr}
              // updateRelateShow={updateRelateShow}
              form={form}/>
          }
          {item.controlType === ControlType.TREE_SELECT.value && <TreeSelect
            propertyItem={item}
            form={form}
            disabled={disabled}
            required={required}
          />}
          {
            // 输入框组件
            item.controlType === ControlType.INPUT.value && <Input
              propertyItem={item}
              disabled={disabled}
              required={required}
              // form={form}
              onChange={(val) => {
                // 如果是机会点名称，且被编辑过时，不会在机会点编辑中点击poi图例时赋值，2024.10.17定制化逻辑-甜瓜
                if (!val.target.value) {
                  setEditedBasicChancePointName?.(false);
                } else if (item.identification === 'basicChancePointName') {
                  setEditedBasicChancePointName?.(true);
                }
              }}
            />
          }
          {
            // 文本框组件
            item.controlType === ControlType.TEXT_AREA.value && <TextareaInput
              propertyItem={item}
              disabled={disabled}
              required={required}
              form={form}
              tiledFormDataRef={tiledFormDataRef}
            />
          }
          {
            // 上传组件
            item.controlType === ControlType.UPLOAD.value && <Upload
              propertyItem={item}
              disabled={disabled}
              required={required}/>
          }
          {
            // 数字输入框组件
            item.controlType === ControlType.INPUT_NUMBER.value && <NumberInput
              propertyItem={item}
              disabled={disabled}
              required={required}
              showHintStr={showHintStr}
              salesFactorOfKWK={salesFactorOfKWK}
              setSalesFactorOfKWK={setSalesFactorOfKWK}
              form={form}
              tiledFormDataRef={tiledFormDataRef}
            />
          }
          {
            // 日期组件
            item.controlType === ControlType.TIME.value && <Time
              propertyItem={item}
              disabled={disabled}
              required={required}/>
          }
          {
            // 详细地址组件：详细地址+经纬度+地图商圈+经营门店
            item.controlType === ControlType.ADDRESS.value && <Address
              form={form}
              formItemData={item}
              wholeFormData={wholeFormData}
              disabled={disabled}
              required={required}
              addressChange={addressChange}
              isAreaProtect={isAreaProtectionAddressLocked || isAreaProtect}
              mapMoveChange={mapMoveChange}
              mapZoomChange={mapZoomChange}
              isChancepoint={isChancepoint}
              isEditedBasicChancePointName={isEditedBasicChancePointName}
              updateCompValue={updateCompValue}
              expansionTaskClusterId={expansionTaskClusterId}
            />
          }
          {
            // 省市区组件（目前的逻辑是省市区处于禁用状态，根据详细地址组件拿到的省市区id进行赋值）
            item.controlType === ControlType.AREA.value && <Provinces
              form={form}
              propertyItem={item}
              required={required}
              // 仅加盟商带址解析失败时允许补选，仍尊重模板/流程自身的只读限制。
              config={{ disabled: !wholeFormData?.franchiseeAddressParseFailed || disabled }}
            />
          }
          {
            // 自定义省市区（独立组件，不和商圈关联）
            item.controlType === ControlType.CUSTOM_AREA.value && <Provinces
              form={form}
              propertyItem={item}
              required={required}
              disabled={disabled}
            />
          }
          {
            // 踩点组件-0727
            item.controlType === ControlType.FOOTPRINT.value && <Footprint
              form={form}
              disabled={disabled || isFootprintValue}
              required={required}
            />
          }
          {
            // 周边查询（目前的逻辑是周边查询组件内不能修改地址，地址自动从详细地址组件中带出，不可修改。之后详细地址结合周边查询配置，获取周边信息）
            item.controlType === ControlType.SURROUND_SEARCH.value && <Surround
              formItemData={item}
              form={form}
              disabled={disabled}
              required={required}
              surroundChange={updateCompValue}/>
          }
          {
            item.controlType === ControlType.SUB_FORM.value && <SubForm
              form={form}
              disabled={disabled}
              required={required}
              formItemData={item}
              tiledFormDataRef={tiledFormDataRef}
              valueChange={updateCompValue}
              // valuePropName={['propertyList', propertyId]}
              // restriction={restriction}
              // label={anotherName}
              // formConfigList={prop.formConfigList}
              // disabled={!editable}
            />
          }
          {
            item.controlType === ControlType.CONTEND_INFO.value && <ContendInfo
              propertyItem={item}
              form={form}
              disabled={disabled}
              required={required}
              contendInfoChange={updateCompValue}
            />
          }
          {
            item.controlType === ControlType.COMPETITIVE_CONDITIONS.value && <CompetitiveConditions
              propertyItem={item}
              form={form}
              disabled={disabled}
              required={required}
              valueChange={updateCompValue}
            />
          }
          {
            // 商圈规划（目前的逻辑是商圈规划组件内不能修改地址，地址自动从详细地址组件中带出，不可修改。之后用详细地址，获取商圈信息）
            item.controlType === ControlType.BUSINESS_PLANNING.value && <BusinessPlanning
              formItemData={item}
              form={form}
              businessPlanValue={businessPlanValue}
              businessPlanChange={updateCompValue}
              disabled={disabled}/>
          }
          {
            // 销售额（目前的逻辑是销售额组件内不能修改地址，地址自动从详细地址组件中带出，不可修改。之后用详细地址，获取销售额）
            item.controlType === ControlType.SALE_AMOUNT.value && <SaleAmount
              form={form}
              formItemData={item}
              required={required}/>
          }
          {
            // 商圈信息（目前的逻辑是商圈规划组件内不能修改地址，地址自动从详细地址组件中带出，不可修改。之后用详细地址，获取商圈信息）
            item.controlType === ControlType.MATCH_BUSINESS_CIRCLE.value && <BusinessInfo
              formItemData={item}
              form={form}
              disabled={disabled}
              businessInfoValue={businessInfoValue}
              setCustomGatheringCategoryForYHT={setCustomGatheringCategoryForYHT}
              disabledArea={disabledArea}
            />
          }
          {
            // 定制组件，配合益禾堂集客点的商圈类型 https://confluence.lanhanba.com/pages/viewpage.action?pageId=132273933
            item.controlType === ControlType.POPULATION_FLOW_YHT.value && <PopulationFlowYHT
              propertyItem={item}
              form={form}
              disabled={disabled}
              required={required}
              customGatheringCategoryForYHT={customGatheringCategoryForYHT}
            />
          }
          {
            // 参考转化率、参考租金
            [ControlType.REFERENCE_CONVERSION.value, ControlType.REFERENCE_RENT.value].includes(item.controlType) && <RefConversion propertyItem={item} />
          }
          {
            // 对标品牌
            item.controlType === ControlType.BENCH_MARKING_BRANDS.value && <BenchBrands
              propertyItem={item}
              form={form}
              disabled={disabled}
              required={required}
              infoChange={updateCompValue}
            />
          }
          {
            // 基诺浦盈亏测算组件
            item.controlType === ControlType.PROFIT_CAlCULATION_JNP.value && <CalculationJNP
              formItemData={item}
              form={form}
              disabled={disabled}
              wholeFormData={wholeFormData}
              infoChange={updateCompValue}
            />
          }
          {
            // 卡旺卡预计门店销售额
            item.controlType === ControlType.ESTIMATE_SALES_KWK.value && <EstimateSalesKWKCom
              propertyItem={item}
              // form={form}
              // addressVal={addressVal}
              // wholeFormData={wholeFormData}
              // infoChange={updateCompValue}
            />
          }
          {/* 益禾堂定制组件-start
          https://confluence.lanhanba.com/pages/viewpage.action?pageId=132274569 */}
          {
            item.controlType === ControlType.ESTIMATE_SALES_TWO_YHT.value && <EstimateSalesTwo
              propertyItem={item}
              required={required}
            />
          }
          {
            item.controlType === ControlType.ESTIMATE_SALES_PERSONAL_YHT.value && <EstimateSalesPersonal
              tiledFormDataRef={tiledFormDataRef}
              propertyItem={item}
              required={required}
              disabled={disabled}
            />
          }
          {
            item.controlType === ControlType.ESTIMATE_SALES_MT_MEAN_YHT.value && <EstimateSalesMTMean
              propertyItem={item}
              disabled={disabled}
              required={required}
            />
          }
          {
            item.controlType === ControlType.ESTIMATE_SALES_MT_MAX_YHT.value && <EstimateSalesMTMax
              propertyItem={item}
              disabled={disabled}
              required={required}
            />
          }
          {
            item.controlType === ControlType.ESTIMATE_SALES_DELIVERY_YHT.value && <EstimateSalesDelivery
              propertyItem={item}
              required={required}
            />
          }
          {
            item.controlType === ControlType.ESTIMATE_SALES_CONCLUSION_YHT.value && <EstimateSalesConclusion
              propertyItem={item}
              form={form}
              disabled={disabled}
              required={required}
              tiledFormDataRef={tiledFormDataRef}
            />
          }
          {/* 益禾堂定制组件-end*/}

          {
            item.controlType === ControlType.FRANCHISEE_COOPERATION_PROGRESS.value && <FranchiseeCooperationProgress
              form={form}
              disabled={disabled}
              required={required}
              formItemData={item}
              tiledFormDataRef={tiledFormDataRef}
              // valueChange={updateCompValue}
              isEdit={isEdit}
              valueChange={updateCompValue}
            />
          }

          {/* 公司成员 */}
          {
            item.controlType === ControlType.COMPANY_MEMBER.value && <CompanyMember
              form={form}
              propertyItem={item}
              required={required}
              disabled={disabled}
              isEdit={isEdit}
              valueChange={updateCompValue}
            />
          }

          {/* 预测入口率 */}
          {
            item.controlType === ControlType.AI_PREDICT_ENTRY_RATE.value && <AIPredictEntryRate
              form={form}
              formItemData={item}
              // required={required}
              disabled={false}
              valueChange={updateCompValue}
            />
          }
          {
            item.controlType === ControlType.AREA_PROTECTION.value && <AreaProtection
              form={form}
              formItemData={item}
              wholeFormData={wholeFormData}
              addressVal={addressVal}
              valueChange={updateCompValue}
              onCollisionChange={onAreaProtectionCollisionChange}
            />
          }
          {
            showAssociatedRecords && item.controlType === ControlType.ASSOCIATED_RECORDS.value && <AssociatedRecords
              propertyItem={item}
              form={form}
              disabled={disabled}
              addressVal={addressVal}
              tiledFormDataRef={tiledFormDataRef}
              updateRelateShow={updateRelateShow}
            />
          }

        </Col>}
    </> : null
  );
};

export default PropertyComponent;
